/**
 * The two origins, in one place.
 *
 * WebMCP federation names origins in three places that must agree exactly:
 * `exposedTo` on the yarn side, `getTools({ fromOrigins })` on the Jig side, and
 * the iframe `src`. A hostname typed as a literal in any of those three drifts
 * silently and the federation simply returns nothing, so no source file outside
 * this module and the `.env` files is allowed to spell a hostname out. Everything
 * else imports {@link ORIGINS} from `@jig/origins`.
 *
 * This file is the environment free half: pure functions plus the local
 * defaults, importable from Node (the Vite configs read it to derive the dev
 * server host and port). The `import.meta.env` read lives in `./index.ts`.
 */

/** Local dev default for the workbench origin. */
export const DEFAULT_JIG_ORIGIN = 'https://jig.localhost:5173';

/** Local dev default for the partner yarn service origin. */
export const DEFAULT_YARNS_ORIGIN = 'https://yarns.localhost:5174';

/** The shape of the `import.meta.env` slice this module cares about. */
export interface OriginEnv {
  readonly VITE_JIG_ORIGIN?: string | undefined;
  readonly VITE_YARNS_ORIGIN?: string | undefined;
}

/** Both origins, resolved and normalised. */
export interface Origins {
  /** The workbench origin, for example `https://jig.localhost:5173`. */
  readonly jig: string;
  /** The partner yarn service origin, for example `https://yarns.localhost:5174`. */
  readonly yarns: string;
}

/** Host and port for a Vite dev server derived from an origin. */
export interface DevServerTarget {
  readonly host: string;
  readonly port: number;
  readonly https: boolean;
}

const HTTP_DEFAULT_PORT = 80;
const HTTPS_DEFAULT_PORT = 443;

/**
 * Parse an origin, rejecting anything that is not a bare `http(s)` origin.
 *
 * A path, a query or a fragment is rejected rather than trimmed: `exposedTo`
 * compares origins, so a value carrying a path is a configuration mistake worth
 * failing loudly on at boot instead of at federation time.
 */
export const normaliseOrigin = (value: string, variableName: string): string => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(
      `${variableName} must be an absolute origin such as https://example.test, received ${JSON.stringify(value)}.`,
    );
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error(
      `${variableName} must use http or https, received ${JSON.stringify(value)}.`,
    );
  }

  const hasExtraPath = url.pathname !== '/' && url.pathname !== '';
  if (hasExtraPath || url.search !== '' || url.hash !== '') {
    throw new Error(
      `${variableName} must be an origin with no path, query or fragment, received ${JSON.stringify(value)}.`,
    );
  }

  return url.origin;
};

const pick = (
  value: string | undefined,
  fallback: string,
  variableName: string,
): string => {
  const trimmed = value?.trim() ?? '';
  return normaliseOrigin(trimmed === '' ? fallback : trimmed, variableName);
};

/**
 * Resolve both origins from an environment slice, falling back to the local dev
 * defaults when a variable is absent or blank.
 */
export const resolveOrigins = (env: OriginEnv): Origins =>
  Object.freeze({
    jig: pick(env.VITE_JIG_ORIGIN, DEFAULT_JIG_ORIGIN, 'VITE_JIG_ORIGIN'),
    yarns: pick(env.VITE_YARNS_ORIGIN, DEFAULT_YARNS_ORIGIN, 'VITE_YARNS_ORIGIN'),
  });

/**
 * Split an origin into the host and port a Vite dev server should bind to, so
 * the Vite configs never spell a hostname out either.
 */
export const devServerTarget = (
  origin: string,
  variableName: string,
): DevServerTarget => {
  const url = new URL(normaliseOrigin(origin, variableName));
  const https = url.protocol === 'https:';
  const port =
    url.port === ''
      ? https
        ? HTTPS_DEFAULT_PORT
        : HTTP_DEFAULT_PORT
      : Number.parseInt(url.port, 10);

  return Object.freeze({ host: url.hostname, port, https });
};
